import { Target, Users, Layers, BarChart3, Palette, LineChart } from 'lucide-react';

const services = [
  {
    icon: Target,
    title: 'Paid Advertising',
    description: 'High-performance campaigns on Meta, Google, LinkedIn & TikTok engineered for maximum ROAS and predictable pipeline growth.',
    gradient: 'from-blue-500 to-cyan-500',
    bgGlow: 'group-hover:shadow-blue-500/20',
  },
  {
    icon: Users,
    title: 'Lead Generation',
    description: 'Multi-channel lead generation systems that deliver qualified prospects to your sales team — consistently and at scale.',
    gradient: 'from-violet-500 to-purple-500',
    bgGlow: 'group-hover:shadow-violet-500/20',
  },
  {
    icon: Layers,
    title: 'Funnel Design',
    description: 'Conversion-optimized landing pages, sales funnels, and nurture sequences that turn cold traffic into paying customers.',
    gradient: 'from-emerald-500 to-teal-500',
    bgGlow: 'group-hover:shadow-emerald-500/20',
  },
  {
    icon: BarChart3,
    title: 'Conversion Optimization',
    description: 'Data-driven CRO backed by analytics, heatmaps, and A/B testing that squeezes more revenue from every visitor.',
    gradient: 'from-amber-500 to-orange-500',
    bgGlow: 'group-hover:shadow-amber-500/20',
  },
  {
    icon: Palette,
    title: 'Creative Strategy',
    description: 'Scroll-stopping ad creatives and copy that resonate with your ideal customer and drive measurable engagement.',
    gradient: 'from-pink-500 to-rose-500',
    bgGlow: 'group-hover:shadow-pink-500/20',
  },
  {
    icon: LineChart,
    title: 'Growth Consulting',
    description: 'Strategic advisory for scaling your marketing engine — from channel mix optimization to budget allocation and beyond.',
    gradient: 'from-indigo-500 to-blue-500',
    bgGlow: 'group-hover:shadow-indigo-500/20',
  },
];

export default function ServicesSection() {
  return (
    <section id="services" data-testid="services-section" className="section-padding bg-vertex-gradient-light relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-vertex-500/5 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-vertex-500/10 border border-vertex-500/20 mb-6">
            <span className="text-vertex-600 text-sm font-semibold">What We Do</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-vertex-900 mb-5">
            Full-Stack Growth
            <span className="gradient-text"> Services</span>
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Every service is designed with one goal: turn your marketing investment into measurable, scalable revenue.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div
              key={service.title}
              className={`group relative bg-white rounded-2xl p-8 border border-gray-100 card-hover ${service.bgGlow} hover:shadow-2xl`}
            >
              {/* Icon */}
              <div className={`inline-flex p-3.5 rounded-2xl bg-gradient-to-br ${service.gradient} mb-6 shadow-lg`}>
                <service.icon className="w-6 h-6 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-display font-bold text-vertex-900 mb-3 group-hover:text-vertex-600 transition-colors duration-300">
                {service.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {service.description}
              </p>

              {/* Hover Border Accent */}
              <div className={`absolute bottom-0 left-8 right-8 h-1 rounded-full bg-gradient-to-r ${service.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
